import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  IconButton, InputAdornment, MenuItem, Paper, Select, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Navigate } from 'react-router-dom';
import { useRole, useUser } from '../context/UserContext';
import { formatDate, ROLE_HELP, ROLE_LABELS, useOrgApi } from '../utils/orgApi';
import { ConstructionLoader } from '../components/ConstructionLoader';
import ActivityLog from '../components/ActivityLog';
import FirmSecurity from '../components/FirmSecurity';

interface Member {
  id: string;
  email: string;
  role: string;
  is_active: boolean;
  last_login: string | null;
  created_at: string | null;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string | null;
  created_at: string | null;
  accept_url?: string;
}

const ROLES = ['admin', 'member', 'viewer'];

/** Copy to the clipboard; resolves false where the browser refuses. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const LinkField: React.FC<{ url: string; onCopied: () => void }> = ({ url, onCopied }) => (
  <TextField
    value={url}
    size="small"
    fullWidth
    InputProps={{
      readOnly: true,
      sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
      endAdornment: (
        <InputAdornment position="end">
          <Tooltip title="Copy link">
            <IconButton
              size="small"
              aria-label="copy invite link"
              onClick={async () => { if (await copyText(url)) onCopied(); }}
            >
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </InputAdornment>
      ),
    }}
    onFocus={(e) => e.target.select()}
  />
);

const Team: React.FC = () => {
  const api = useOrgApi();
  const { user, org, refreshUser, userLoaded } = useUser();
  const { isAdmin } = useRole();

  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [nameDraft, setNameDraft] = useState('');
  const [editingName, setEditingName] = useState(false);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [lastInvite, setLastInvite] = useState<Invitation | null>(null);

  const [removeTarget, setRemoveTarget] = useState<Member | null>(null);
  const [tab, setTab] = useState<'members' | 'activity'>('members');

  const load = useCallback(async () => {
    const [m, inv] = await Promise.all([
      api<Member[]>('GET', '/org/members'),
      api<Invitation[]>('GET', '/org/invitations'),
    ]);
    if (m.ok) setMembers(m.data || []);
    if (inv.ok) setInvitations(inv.data || []);
    if (!m.ok) setError(m.error || 'Could not load the team.');
    setLoading(false);
  }, [api]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  useEffect(() => {
    if (org?.name) setNameDraft(org.name);
  }, [org?.name]);

  if (userLoaded && !org) {
    return (
      <Container maxWidth={false} sx={{ maxWidth: 1000, mt: 4 }}>
        <Alert severity="error">Could not load your firm. Refresh the page to try again.</Alert>
      </Container>
    );
  }
  if (!userLoaded || !org) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <ConstructionLoader />
      </Box>
    );
  }
  // The nav hides Team from non-admins; a typed URL lands back home.
  if (!isAdmin) return <Navigate to="/" replace />;

  const flash = (msg: string) => {
    setError(null);
    setNotice(msg);
  };
  const fail = (msg?: string) => {
    setNotice(null);
    setError(msg || 'Something went wrong. Please try again.');
  };

  const afterChange = async () => {
    await Promise.all([load(), refreshUser()]);
  };

  const saveName = async () => {
    const r = await api('PATCH', '/org', { name: nameDraft });
    if (!r.ok) return fail(r.error);
    setEditingName(false);
    flash('Firm name saved.');
    await refreshUser();
  };

  const changeRole = async (m: Member, role: string) => {
    if (role === m.role) return;
    const r = await api('PATCH', `/org/members/${m.id}`, { role });
    if (!r.ok) return fail(r.error);
    flash(`${m.email} is now ${ROLE_LABELS[role] || role}.`);
    await afterChange();
  };

  const confirmRemove = async () => {
    const m = removeTarget;
    setRemoveTarget(null);
    if (!m) return;
    const r = await api('DELETE', `/org/members/${m.id}`);
    if (!r.ok) return fail(r.error);
    flash(`${m.email} was removed. The deals they created stay with the firm.`);
    await afterChange();
  };

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    const r = await api<Invitation>('POST', '/org/invitations', { email, role: inviteRole });
    setInviting(false);
    if (!r.ok) return fail(r.error);
    setLastInvite(r.data);
    setInviteEmail('');
    flash(r.status === 201
      ? `Invitation created for ${email}. Copy the link below and send it to them.`
      : `${email} already has a pending invitation. Here is the same link again.`);
    await afterChange();
  };

  const revoke = async (inv: Invitation) => {
    const r = await api('DELETE', `/org/invitations/${inv.id}`);
    if (!r.ok) return fail(r.error);
    if (lastInvite?.id === inv.id) setLastInvite(null);
    flash(`The invitation to ${inv.email} was revoked; its link no longer works.`);
    await afterChange();
  };

  const seatText = org.seat_limit
    ? `${org.seats_used} of ${org.seat_limit} seats used`
    : `${org.seats_used} ${org.seats_used === 1 ? 'person' : 'people'}`;

  return (
    <Container maxWidth={false} sx={{ maxWidth: 1000, width: '100%', mt: 4, backgroundColor: 'white', p: 2, borderRadius: 2 }}>
      <Box sx={{ mt: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Team</Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Everyone in your firm sees every deal. Roles decide who can change them.
        </Typography>

        {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice}</Alert>}
        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab value="members" label="Members" />
          <Tab value="activity" label="Activity" />
        </Tabs>

        {tab === 'activity' ? (
          <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Activity</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Who did what in your firm: deals created, saved, exported and deleted, notes and pictures,
              pipeline stages, team changes, company settings and sign-ins. The log is permanent and
              cannot be edited or cleared.
            </Typography>
            <ActivityLog scope="org" people={members.map((m) => ({ id: m.id, email: m.email }))} />
          </Paper>
        ) : (
        <>
        {/* Firm */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Firm</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, rowGap: 1.5, columnGap: 2, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Firm name</Typography>
            {editingName ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField size="small" value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} inputProps={{ maxLength: 120 }} sx={{ flex: 1, maxWidth: 420 }} />
                <Button variant="contained" size="small" onClick={saveName} disabled={!nameDraft.trim()}>Save</Button>
                <Button size="small" onClick={() => { setEditingName(false); setNameDraft(org.name); }}>Cancel</Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{org.name}</Typography>
                <Button variant="outlined" size="small" onClick={() => setEditingName(true)}>Rename</Button>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary">Seats</Typography>
            <Typography variant="body1">
              {seatText}
              {org.pending_invitations > 0 ? `, ${org.pending_invitations} invitation${org.pending_invitations === 1 ? '' : 's'} pending` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">Plan</Typography>
            <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
              {org.plan_tier}{org.billing_mode === 'invoice' ? ' (invoiced)' : ''}
            </Typography>
          </Box>
        </Paper>

        <FirmSecurity org={org} onChanged={refreshUser} flash={flash} fail={fail} />

        {/* Invite */}
        <Paper variant="outlined" sx={{ p: 3, mt: 4 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Invite someone</Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creates a sign-up link for one email address, valid for 14 days. UnderwritRE does not email it for you:
            copy the link and send it yourself. They join the firm when they sign in with that address.
          </Typography>
          <Box component="form" onSubmit={sendInvite} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              size="small"
              type="email"
              label="Email address"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              sx={{ flex: '1 1 260px' }}
              required
            />
            <Select size="small" value={inviteRole} onChange={(e) => setInviteRole(e.target.value as string)} sx={{ minWidth: 140 }}>
              {ROLES.map((r) => <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>)}
            </Select>
            <Button type="submit" variant="contained" disabled={inviting || !inviteEmail.trim()} sx={{ height: 40 }}>
              Create invite link
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {ROLE_LABELS[inviteRole]}: {ROLE_HELP[inviteRole]}
          </Typography>
          {lastInvite?.accept_url && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.75 }}>
                Link for <strong>{lastInvite.email}</strong> ({ROLE_LABELS[lastInvite.role]}), expires {formatDate(lastInvite.expires_at)}:
              </Typography>
              <LinkField url={lastInvite.accept_url} onCopied={() => flash('Link copied.')} />
            </Box>
          )}
        </Paper>

        {/* Members */}
        <Paper variant="outlined" sx={{ p: 3, mt: 4 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Members</Typography>
          <Divider sx={{ mb: 2 }} />
          {loading ? (
            <ConstructionLoader />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell sx={{ width: 160 }}>Role</TableCell>
                    <TableCell sx={{ width: 130 }}>Last sign-in</TableCell>
                    <TableCell sx={{ width: 100 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((m) => {
                    const isMe = m.email.toLowerCase() === (user?.email || '').toLowerCase();
                    return (
                      <TableRow key={m.id}>
                        <TableCell>
                          {m.email}
                          {isMe && <Chip label="You" size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />}
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            value={m.role}
                            onChange={(e) => changeRole(m, e.target.value as string)}
                            sx={{ minWidth: 130, fontSize: '0.875rem' }}
                            inputProps={{ 'aria-label': `role for ${m.email}` }}
                          >
                            {ROLES.map((r) => <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>)}
                          </Select>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{formatDate(m.last_login)}</TableCell>
                        <TableCell align="right">
                          {!isMe && (
                            <Button size="small" color="error" onClick={() => setRemoveTarget(m)}>Remove</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Pending invitations */}
        <Paper variant="outlined" sx={{ p: 3, mt: 4, mb: 2 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Pending invitations</Typography>
          <Divider sx={{ mb: 2 }} />
          {invitations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No pending invitations.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell sx={{ width: 100 }}>Role</TableCell>
                    <TableCell sx={{ width: 130 }}>Expires</TableCell>
                    <TableCell sx={{ width: 190 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell>{inv.email}</TableCell>
                      <TableCell>{ROLE_LABELS[inv.role] || inv.role}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{formatDate(inv.expires_at)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {inv.accept_url && (
                          <Button
                            size="small"
                            startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                            onClick={async () => {
                              if (await copyText(inv.accept_url as string)) flash(`Link for ${inv.email} copied.`);
                              else setLastInvite(inv);
                            }}
                          >
                            Copy link
                          </Button>
                        )}
                        <Button size="small" color="error" onClick={() => revoke(inv)}>Revoke</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
        </>
        )}
      </Box>

      <Dialog open={!!removeTarget} onClose={() => setRemoveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove {removeTarget?.email}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            They lose access to the firm's deals straight away. The deals they created stay with the firm.
            They keep their UnderwritRE login and go back to an account of their own.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmRemove}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Team;
